import React, { useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { newsItems, accounts, notifications, userProfile } from '../../data/mockData';
import Avatar from '../ui/Avatar';
import SearchInput from '../ui/SearchInput';
import avatarImg from '../../assets/avtar.png';

const TITLES = {
  '/': 'Dashboard',
  '/news': 'Insights',
  '/list': 'Accounts',
  '/user': 'Profile',
};

export default function Topbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const searchRef = useRef(null);
  const notifRef = useRef(null);
  useOnClickOutside(searchRef, () => setSearchOpen(false));
  useOnClickOutside(notifRef, () => setNotifOpen(false));

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const newsMatches = newsItems
      .filter((n) => n.title.toLowerCase().includes(q))
      .map((n) => ({ id: `news-${n.id}`, label: n.title, type: 'Insight', to: '/news' }));
    const accountMatches = accounts
      .filter((a) => a.title.toLowerCase().includes(q))
      .map((a) => ({ id: `acc-${a.id}`, label: a.title, type: 'Account', to: '/list' }));
    return [...newsMatches, ...accountMatches].slice(0, 6);
  }, [query]);

  const handleSelect = (result) => {
    setQuery('');
    setSearchOpen(false);
    navigate(`${result.to}?q=${encodeURIComponent(result.label)}`);
  };

  return (
    <header className="sticky top-0 z-20 flex items-center gap-4 border-b border-border bg-bg/80 backdrop-blur-md px-4 py-3 md:px-6">
      <h1 className="text-lg font-semibold text-primary shrink-0">{TITLES[pathname] || 'RapidQuest'}</h1>

      <div ref={searchRef} className="relative ml-auto hidden flex-1 max-w-sm sm:block">
        <SearchInput
          id="global-search"
          label="Search accounts and insights"
          placeholder="Search accounts, insights..."
          value={query}
          onChange={(v) => {
            setQuery(v);
            setSearchOpen(true);
          }}
        />
        {searchOpen && query.trim() && (
          <div className="absolute left-0 right-0 top-full mt-2 overflow-hidden rounded-xl border border-border bg-surface shadow-card-hover animate-scale-in">
            {results.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted">No matches for "{query}"</p>
            ) : (
              <ul role="listbox">
                {results.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(r)}
                      className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-primary hover:bg-elevated"
                    >
                      <span>{r.label}</span>
                      <span className="text-xs text-muted">{r.type}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-2 sm:ml-0">
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="flex h-9 w-9 items-center justify-center rounded-xl text-secondary hover:bg-elevated hover:text-primary transition-colors"
        >
          {isDark ? <Sun className="h-4.5 w-4.5" aria-hidden="true" /> : <Moon className="h-4.5 w-4.5" aria-hidden="true" />}
        </button>

        <div ref={notifRef} className="relative">
          <button
            type="button"
            onClick={() => setNotifOpen((o) => !o)}
            aria-label={`Notifications (${notifications.length} unread)`}
            aria-expanded={notifOpen}
            className="relative flex h-9 w-9 items-center justify-center rounded-xl text-secondary hover:bg-elevated hover:text-primary transition-colors"
          >
            <Bell className="h-4.5 w-4.5" aria-hidden="true" />
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-danger ring-2 ring-bg" />
          </button>
          {notifOpen && (
            <div className="absolute right-0 top-full mt-2 w-72 overflow-hidden rounded-xl border border-border bg-surface shadow-card-hover animate-scale-in">
              <div className="border-b border-border px-4 py-3">
                <p className="text-sm font-semibold text-primary">Notifications</p>
              </div>
              <ul className="max-h-80 overflow-y-auto">
                {notifications.map((n) => (
                  <li key={n.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    <p className="text-sm font-medium text-primary">{n.title}</p>
                    <p className="mt-0.5 text-xs text-secondary">{n.detail}</p>
                    <p className="mt-1 text-[11px] text-muted">{n.time}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <Link
          to="/user"
          aria-label="View profile"
          className="rounded-full transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
        >
          <Avatar src={avatarImg} name={userProfile.name} size="sm" />
        </Link>
      </div>
    </header>
  );
}
